import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from '#environments';
import {
  NrAttachment,
  NrAttachmentUrl,
  NrAuditLogEntry,
  NrCaseDetail,
  NrCaseListQuery,
  NrCasePage,
  NrCaseStatusRequest,
  NrConservationCheckResult,
  CreateNrCaseRequest,
  UpdateNrCaseRequest,
} from '#models';

@Injectable({
  providedIn: 'root',
})
export class NrCasesApiService {
  private _http = inject(HttpClient);
  private readonly _baseUrl = environment.apiUrl;

  // ─── Open / Closed lists ──────────────────────────────────────────────────

  getOpenCases(query?: NrCaseListQuery): Observable<NrCasePage> {
    let params = new HttpParams();
    if (query?.page != null) params = params.set('page', query.page);
    if (query?.pageSize != null) params = params.set('pageSize', query.pageSize);
    if (query?.sort) params = params.set('sort', query.sort);
    if (query?.desc != null) params = params.set('desc', query.desc);
    return this._http.get<NrCasePage>(`${this._baseUrl}/nr-cases/open`, { params });
  }

  getClosedCases(query?: NrCaseListQuery): Observable<NrCasePage> {
    let params = new HttpParams();
    if (query?.page != null) params = params.set('page', query.page);
    if (query?.pageSize != null) params = params.set('pageSize', query.pageSize);
    if (query?.sort) params = params.set('sort', query.sort);
    if (query?.desc != null) params = params.set('desc', query.desc);
    return this._http.get<NrCasePage>(`${this._baseUrl}/nr-cases/closed`, { params });
  }

  // ─── Single case ─────────────────────────────────────────────────────────

  getCaseById(id: number): Observable<NrCaseDetail> {
    return this._http.get<NrCaseDetail>(`${this._baseUrl}/nr-cases/${id}`);
  }

  getCaseByCaseNumber(caseNumber: string): Observable<NrCaseDetail> {
    return this._http.get<NrCaseDetail>(`${this._baseUrl}/nr-cases/by-case-number/${encodeURIComponent(caseNumber)}`);
  }

  // ─── Create / Update ─────────────────────────────────────────────────────

  createCase(request: CreateNrCaseRequest): Observable<NrCaseDetail> {
    return this._http.post<NrCaseDetail>(`${this._baseUrl}/nr-cases`, request);
  }

  updateCase(id: number, request: UpdateNrCaseRequest): Observable<NrCaseDetail> {
    return this._http.put<NrCaseDetail>(`${this._baseUrl}/nr-cases/${id}`, request);
  }

  // ─── Status transition ────────────────────────────────────────────────────

  applyStatus(id: number, request: NrCaseStatusRequest): Observable<NrCaseDetail> {
    return this._http.patch<NrCaseDetail>(`${this._baseUrl}/nr-cases/${id}/status`, request);
  }

  reopen(id: number): Observable<NrCaseDetail> {
    return this._http.patch<NrCaseDetail>(`${this._baseUrl}/nr-cases/${id}/reopen`, {});
  }

  deleteCase(id: number): Observable<void> {
    return this._http.delete<void>(`${this._baseUrl}/nr-cases/${id}`);
  }

  // ─── Conservation check ───────────────────────────────────────────────────

  getConservationCheck(id: number): Observable<NrConservationCheckResult> {
    return this._http.get<NrConservationCheckResult>(
      `${this._baseUrl}/nr-cases/${id}/conservation-check`,
    );
  }

  // ─── Attachments ─────────────────────────────────────────────────────────

  getAttachments(id: number): Observable<NrAttachment[]> {
    return this._http.get<NrAttachment[]>(`${this._baseUrl}/nr-cases/${id}/attachments`);
  }

  uploadAttachment(id: number, file: File, attachmentType: string): Observable<NrAttachment> {
    const formData = new FormData();
    formData.append('file', file, file.name);
    formData.append('attachmentType', attachmentType);
    return this._http.post<NrAttachment>(
      `${this._baseUrl}/nr-cases/${id}/attachments`,
      formData,
    );
  }

  deleteAttachment(id: number, attachmentId: number): Observable<void> {
    return this._http.delete<void>(
      `${this._baseUrl}/nr-cases/${id}/attachments/${attachmentId}`,
    );
  }

  getAttachmentUrl(id: number, attachmentId: number): Observable<NrAttachmentUrl> {
    return this._http.get<NrAttachmentUrl>(
      `${this._baseUrl}/nr-cases/${id}/attachments/${attachmentId}/download-url`,
    );
  }

  // ─── Audit Log ──────────────────────────────────────────────────────────────

  getAuditLog(id: number): Observable<NrAuditLogEntry[]> {
    return this._http.get<NrAuditLogEntry[]>(`${this._baseUrl}/nr-cases/${id}/audit-log`);
  }
}
